/**
 * Stale-bundle recovery.
 *
 * WHAT BREAKS. `nginx.conf` serves `/assets/` as `immutable` for 7 days but
 * leaves `index.html` on the browser's heuristic cache. After a rebuild the
 * cached HTML still names the previous chunk hashes, so the first lazy route
 * the user opens throws `Failed to fetch dynamically imported module` against
 * a file that no longer exists. Every `React.lazy` route in `router.tsx` is
 * exposed to it, and it lands on real users after every deploy, not only on a
 * developer mid-rebuild.
 *
 * WHY A RELOAD AND NOT A REDIRECT. The dead bundle is already evaluated and in
 * memory, so a client-side navigation carries it along: `/` happens to render
 * (Dashboard is imported eagerly) and then the next lazy route fails
 * identically. Only a document load re-fetches `index.html` and with it the
 * current chunk names. That is why this module exists instead of a toast.
 *
 * WHY A COOLDOWN AND NOT A ONE-SHOT FLAG. A plain "already tried" flag has to
 * be cleared by someone, and every place you could clear it either reopens the
 * infinite loop or closes recovery for the rest of the session. A timestamp
 * needs no clearing: a genuinely broken deploy reloads once and then falls
 * through to the error page, while a stale bundle an hour later is still
 * recoverable. `sessionStorage` rather than `localStorage` so it dies with the
 * tab.
 */

const RELOAD_KEY = 'groundwork_chunk_reload_at'

/** Two failures inside this window mean the reload did not help. */
export const RELOAD_COOLDOWN_MS = 30_000

/**
 * Every phrasing the browsers emit for the same failure. Vite's own
 * `vite:preloadError` covers the preload path; these are what actually reaches
 * an error boundary. Chrome and Firefox differ on wording, Safari differs
 * again, and none of them expose a machine-readable code.
 */
const CHUNK_ERROR =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css/i

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return CHUNK_ERROR.test(message)
}

/**
 * Pure read: may we still reload? Kept separate from `beginChunkReload` so the
 * render pass can decide WHAT to show without performing a side effect.
 */
export function chunkReloadAllowed(now: number = Date.now()): boolean {
  try {
    const raw = sessionStorage.getItem(RELOAD_KEY)
    // "No record" and "unreadable record" are both explicitly allowed, rather
    // than left to fall out of the arithmetic. Leaning on `Number(null) === 0`
    // makes the answer depend on how large `now` happens to be, which is true
    // for a real clock and false for any injected one.
    if (raw === null) return true
    const last = Number(raw)
    if (!Number.isFinite(last)) return true
    return now - last >= RELOAD_COOLDOWN_MS
  } catch {
    // Storage can throw outright (Safari private mode, blocked site data).
    // Refusing to reload is the safe direction: the error page still renders.
    return false
  }
}

/**
 * Check and claim in one step: record that this tab is about to reload, and say
 * whether the caller may.
 *
 * IT DOES NOT RELOAD. Navigation is the component's job, so this module stays
 * pure policy over `sessionStorage` and can be tested without stubbing
 * `window.location`. It also means the caller can claim during its render pass
 * -- an idempotent, guarded, local write -- and be left with nothing but the
 * navigation to perform in an effect. Deciding in an effect instead would mean
 * writing the answer back with `setState`, which cascades renders and is what
 * `react-hooks/set-state-in-effect` correctly refuses.
 *
 * Atomic on purpose: StrictMode renders and re-runs effects twice, and the
 * second pass must see the claim the first one made rather than issue a second
 * reload.
 */
export function claimChunkReload(now: number = Date.now()): boolean {
  if (!chunkReloadAllowed(now)) return false
  try {
    sessionStorage.setItem(RELOAD_KEY, String(now))
  } catch {
    // Readable but not writable. Refusing here is what keeps the caller from
    // showing a recovery notice for a reload that would never be recorded, and
    // so could repeat forever.
    return false
  }
  return true
}
